'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { FilterParams } from '@/lib/search-filter-params';
import { filterLocationsForCountry } from './searchConstants';

/**
 * The sidebar's staged filter values: one `useState` per FilterParams field,
 * seeded from the applied `filterParams` and re-synced whenever the applied
 * URL changes. The sync effect lives here because it is the one place that
 * writes every field; the setters it calls must be declared in this scope.
 */
export function useSidebarFilterValues(
  filterParams: FilterParams | undefined,
  preserveCountryEdits: boolean,
) {
  // Initialize from filterParams prop
  const [searchInput, setSearchInput] = useState(filterParams?.q ?? '');
  const [country, setCountry] = useState<string>(filterParams?.country ?? '');
  const [selectedCities, setSelectedCities] = useState<string[]>(filterParams?.city ?? []);
  const [currentRegisterId, setCurrentRegisterId] = useState(filterParams?.registerId ?? '');
  const [currentRegisterCourt, setCurrentRegisterCourt] = useState(filterParams?.registerCourt ?? '');
  const [currentRegisterType, setCurrentRegisterType] = useState(filterParams?.registerType ?? '');
  const [currentEuid, setCurrentEuid] = useState(filterParams?.euid ?? '');
  const [hasYearlyMetadata, setHasYearlyMetadata] = useState<boolean>(filterParams?.hasYearlyMetadata ?? false);
  const [selectedBundesland, setSelectedBundesland] = useState<string[]>(filterParams?.Bundesland ?? []);
  const [revenueMin, setRevenueMin] = useState<number | undefined>(filterParams?.revenueMin);
  const [revenueMax, setRevenueMax] = useState<number | undefined>(filterParams?.revenueMax);
  const [revenueIncludeNull, setRevenueIncludeNull] = useState<boolean>(filterParams?.revenueIncludeNull ?? false);
  const [aktivaMin, setAktivaMin] = useState<number | undefined>(filterParams?.aktivaMin);
  const [aktivaMax, setAktivaMax] = useState<number | undefined>(filterParams?.aktivaMax);
  const [financialYear, setFinancialYear] = useState<number | undefined>(filterParams?.financialYear);
  const [profitMin, setProfitMin] = useState<number | undefined>(filterParams?.profitMin);
  const [profitMax, setProfitMax] = useState<number | undefined>(filterParams?.profitMax);
  const [employeesMin, setEmployeesMin] = useState<number | undefined>(filterParams?.employeesMin);
  const [employeesMax, setEmployeesMax] = useState<number | undefined>(filterParams?.employeesMax);
  const [selectedAgeRanges, setSelectedAgeRanges] = useState<string[]>(filterParams?.ageRanges ?? []);
  const [selectedNaceCodes, setSelectedNaceCodes] = useState<string[]>(filterParams?.naceCodes ?? []);
  // Industries are an independent filter — sent to the backend as a separate
  // parameter that gets expanded to NACE divisions there. The NACE-Codes
  // selector below is unaffected by industry toggles, and vice versa.
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(filterParams?.industries ?? []);
  const toggleIndustry = useCallback((key: string) => {
    setSelectedIndustries((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }, []);
  const [foundedAtFrom, setFoundedAtFrom] = useState(filterParams?.foundedAtFrom ?? '');
  const [foundedAtTo, setFoundedAtTo] = useState(filterParams?.foundedAtTo ?? '');
  const [tags, setTags] = useState<string[]>(filterParams?.tags ?? []);
  const [personName, setPersonName] = useState(filterParams?.personName ?? '');
  const [cpvCode, setCpvCode] = useState(filterParams?.cpvCode ?? '');
  const [selectedRechtsformen, setSelectedRechtsformen] = useState<string[]>(filterParams?.rechtsformCodes ?? []);
  const [selectedMemberships, setSelectedMemberships] = useState<string[]>(filterParams?.memberships ?? []);
  const [selectedLegalStatus, setSelectedLegalStatus] = useState<string[]>(filterParams?.legalStatus ?? []);
  const [selectedCompanySize, setSelectedCompanySize] = useState<string[]>(filterParams?.companySize ?? []);
  const [selectedTenderRole, setSelectedTenderRole] = useState<string[]>(filterParams?.tenderRole ?? []);
  const [familyManaged, setFamilyManaged] = useState<boolean | undefined>(filterParams?.familyManaged);
  const [founderLed, setFounderLed] = useState<boolean | undefined>(filterParams?.founderLed);
  const [successionRisk, setSuccessionRisk] = useState<boolean | undefined>(filterParams?.successionRisk);
  const [leiCode, setLeiCode] = useState(filterParams?.leiCode ?? '');
  const [ustIdnr, setUstIdnr] = useState(filterParams?.ustIdnr ?? '');
  const [hasWebsite, setHasWebsite] = useState<boolean>(filterParams?.hasWebsite ?? false);
  const [hasEmail, setHasEmail] = useState<boolean>(filterParams?.hasEmail ?? false);

  const previousAppliedFilters = useRef(filterParams);

  // Staged edits leave filterParams unchanged. Sync whenever the applied URL
  // changes, including chip removals and back/forward navigation.
  useEffect(() => {
    const previous = previousAppliedFilters.current;
    previousAppliedFilters.current = filterParams;
    if (filterParams) {
      const nextCountry = filterParams.country ?? '';
      // A scope-only change applies immediately but keeps the other staged
      // edits. Narrow staged locations just like the applied result filters.
      const expected = previous && {
        ...previous,
        country: nextCountry,
        Bundesland: filterLocationsForCountry(previous.Bundesland, nextCountry),
        city: nextCountry ? [] : previous.city,
      };
      if (
        preserveCountryEdits &&
        previous &&
        previous.country !== nextCountry &&
        Object.keys(filterParams).every(
          (key) =>
            JSON.stringify(filterParams[key as keyof FilterParams]) ===
            JSON.stringify(expected?.[key as keyof FilterParams]),
        )
      ) {
        setCountry(nextCountry); // eslint-disable-line react-hooks/set-state-in-effect -- staged filters re-seed from the applied URL after it changes (pre-existing; a try/finally in the old component hid it from the compiler)
        setSelectedBundesland((staged) =>
          filterLocationsForCountry(staged, nextCountry),
        );
        if (nextCountry) setSelectedCities([]);
        return;
      }
      setSearchInput(filterParams.q);
      setSelectedCities(filterParams.city);
      setCurrentRegisterId(filterParams.registerId);
      setCurrentRegisterCourt(filterParams.registerCourt);
      setCurrentRegisterType(filterParams.registerType ?? '');
      setCurrentEuid(filterParams.euid);
      setHasYearlyMetadata(filterParams.hasYearlyMetadata ?? false);
      setSelectedBundesland(filterParams.Bundesland);
      setRevenueMin(filterParams.revenueMin);
      setRevenueMax(filterParams.revenueMax);
      setRevenueIncludeNull(filterParams.revenueIncludeNull ?? false);
      setAktivaMin(filterParams.aktivaMin);
      setAktivaMax(filterParams.aktivaMax);
      setFinancialYear(filterParams.financialYear);
      setProfitMin(filterParams.profitMin);
      setProfitMax(filterParams.profitMax);
      setEmployeesMin(filterParams.employeesMin);
      setEmployeesMax(filterParams.employeesMax);
      setSelectedAgeRanges(filterParams.ageRanges);
      setSelectedNaceCodes(filterParams.naceCodes);
      setSelectedRechtsformen(filterParams.rechtsformCodes);
      setSelectedIndustries(filterParams.industries ?? []);
      setFoundedAtFrom(filterParams.foundedAtFrom ?? '');
      setFoundedAtTo(filterParams.foundedAtTo ?? '');
      setTags(filterParams.tags ?? []);
      setPersonName(filterParams.personName ?? '');
      setCpvCode(filterParams.cpvCode ?? '');
      setSelectedMemberships(filterParams.memberships ?? []);
      setSelectedLegalStatus(filterParams.legalStatus ?? []);
      setSelectedCompanySize(filterParams.companySize ?? []);
      setCountry(filterParams.country ?? '');
      setSelectedTenderRole(filterParams.tenderRole ?? []);
      setFamilyManaged(filterParams.familyManaged);
      setFounderLed(filterParams.founderLed);
      setSuccessionRisk(filterParams.successionRisk);
      setLeiCode(filterParams.leiCode ?? '');
      setUstIdnr(filterParams.ustIdnr ?? '');
      setHasWebsite(filterParams.hasWebsite ?? false);
      setHasEmail(filterParams.hasEmail ?? false);
    }
  }, [filterParams, preserveCountryEdits]);

  return {
    searchInput, setSearchInput, country, setCountry, selectedCities, setSelectedCities,
    currentRegisterId, setCurrentRegisterId, currentRegisterCourt, setCurrentRegisterCourt,
    currentRegisterType, setCurrentRegisterType, currentEuid, setCurrentEuid,
    hasYearlyMetadata, setHasYearlyMetadata, selectedBundesland, setSelectedBundesland,
    revenueMin, setRevenueMin, revenueMax, setRevenueMax, revenueIncludeNull, setRevenueIncludeNull,
    aktivaMin, setAktivaMin, aktivaMax, setAktivaMax, financialYear, setFinancialYear,
    profitMin, setProfitMin, profitMax, setProfitMax, employeesMin, setEmployeesMin,
    employeesMax, setEmployeesMax, selectedAgeRanges, setSelectedAgeRanges,
    selectedNaceCodes, setSelectedNaceCodes, selectedIndustries, setSelectedIndustries, toggleIndustry,
    foundedAtFrom, setFoundedAtFrom, foundedAtTo, setFoundedAtTo, tags, setTags,
    personName, setPersonName, cpvCode, setCpvCode, selectedRechtsformen, setSelectedRechtsformen,
    selectedMemberships, setSelectedMemberships, selectedLegalStatus, setSelectedLegalStatus,
    selectedCompanySize, setSelectedCompanySize, selectedTenderRole, setSelectedTenderRole,
    familyManaged, setFamilyManaged, founderLed, setFounderLed, successionRisk, setSuccessionRisk,
    leiCode, setLeiCode, ustIdnr, setUstIdnr, hasWebsite, setHasWebsite, hasEmail, setHasEmail,
  };
}

export type SidebarFilterValues = ReturnType<typeof useSidebarFilterValues>;

/** The staged values as one FilterParams — what Search, Save and the footer emit. */
export function useCollectFilters(values: SidebarFilterValues) {
  const {
    searchInput, selectedCities, currentRegisterId, currentRegisterCourt, currentRegisterType,
    currentEuid, selectedBundesland, revenueMin, revenueMax, revenueIncludeNull, aktivaMin,
    aktivaMax, financialYear, profitMin, profitMax, employeesMin, employeesMax, selectedAgeRanges,
    selectedNaceCodes, selectedIndustries, foundedAtFrom, foundedAtTo, tags, personName,
    selectedRechtsformen, cpvCode, hasYearlyMetadata, selectedMemberships, selectedLegalStatus,
    selectedCompanySize, country, selectedTenderRole, familyManaged, founderLed, successionRisk,
    leiCode, ustIdnr, hasWebsite, hasEmail,
  } = values;
  // Collect current filter state
  const collectFilters = useCallback(
    (): FilterParams => ({
      q: searchInput,
      city: selectedCities,
      registerId: currentRegisterId,
      registerCourt: currentRegisterCourt,
      registerType: currentRegisterType,
      euid: currentEuid,
      Bundesland: selectedBundesland,
      revenueMin,
      revenueMax,
      revenueIncludeNull,
      aktivaMin,
      aktivaMax,
      financialYear,
      profitMin,
      profitMax,
      employeesMin,
      employeesMax,
      ageRanges: selectedAgeRanges,
      naceCodes: selectedNaceCodes,
      industries: selectedIndustries,
      foundedAtFrom,
      foundedAtTo,
      tags,
      personName,
      rechtsformCodes: selectedRechtsformen,
      cpvCode,
      hasYearlyMetadata,
      memberships: selectedMemberships,
      legalStatus: selectedLegalStatus,
      companySize: selectedCompanySize,
      country,
      tenderRole: selectedTenderRole,
      familyManaged,
      founderLed,
      successionRisk,
      leiCode,
      ustIdnr,
      hasWebsite,
      hasEmail,
    }),
    [
      searchInput,
      selectedCities,
      currentRegisterId,
      currentRegisterCourt,
      currentRegisterType,
      currentEuid,
      selectedBundesland,
      revenueMin,
      revenueMax,
      revenueIncludeNull,
      aktivaMin,
      aktivaMax,
      financialYear,
      profitMin,
      profitMax,
      employeesMin,
      employeesMax,
      selectedAgeRanges,
      selectedNaceCodes,
      selectedIndustries,
      foundedAtFrom,
      foundedAtTo,
      tags,
      personName,
      selectedRechtsformen,
      cpvCode,
      hasYearlyMetadata,
      selectedMemberships,
      selectedLegalStatus,
      selectedCompanySize,
      country,
      selectedTenderRole,
      familyManaged,
      founderLed,
      successionRisk,
      leiCode,
      ustIdnr,
      hasWebsite,
      hasEmail,
    ],
  );
  return collectFilters;
}

/** Per-group active filter counts for the group headers (computed each render). */
export function countActiveFilters(values: SidebarFilterValues) {
  const {
    selectedCities, selectedBundesland, selectedNaceCodes, selectedIndustries, tags, cpvCode,
    revenueMin, revenueMax, aktivaMin, aktivaMax, profitMin, profitMax, employeesMin, employeesMax,
    financialYear, hasYearlyMetadata, selectedRechtsformen, foundedAtFrom, foundedAtTo,
    selectedMemberships, selectedLegalStatus, selectedTenderRole, hasWebsite, hasEmail, personName,
    selectedAgeRanges, familyManaged, founderLed, successionRisk, currentEuid, currentRegisterId,
    currentRegisterCourt, currentRegisterType, leiCode, ustIdnr,
  } = values;
  // Per-group active filter counts. Each non-empty field counts once. The
  // company-name and ID-lookup inputs sit outside any group and aren't counted.
  const locationCount =
    (selectedCities.length > 0 ? 1 : 0) +
    (selectedBundesland.length > 0 ? 1 : 0);
  const industryCount =
    (selectedNaceCodes.length > 0 ? 1 : 0) +
    (selectedIndustries.length > 0 ? 1 : 0) +
    (tags.length > 0 ? 1 : 0) +
    (cpvCode ? 1 : 0);
  const sizeCount =
    ((revenueMin !== undefined || revenueMax !== undefined) ? 1 : 0) +
    ((aktivaMin !== undefined || aktivaMax !== undefined) ? 1 : 0) +
    ((profitMin !== undefined || profitMax !== undefined) ? 1 : 0) +
    ((employeesMin !== undefined || employeesMax !== undefined) ? 1 : 0) +
    (financialYear != null ? 1 : 0) +
    (hasYearlyMetadata ? 1 : 0);
  const profileCount =
    (selectedRechtsformen.length > 0 ? 1 : 0) +
    (foundedAtFrom ? 1 : 0) +
    (foundedAtTo ? 1 : 0) +
    (selectedMemberships.length > 0 ? 1 : 0) +
    (selectedLegalStatus.length > 0 ? 1 : 0) +
    (selectedTenderRole.length > 0 ? 1 : 0) +
    (hasWebsite ? 1 : 0) +
    (hasEmail ? 1 : 0);
  const peopleCount =
    (personName ? 1 : 0) +
    (selectedAgeRanges.length > 0 ? 1 : 0) +
    (familyManaged !== undefined ? 1 : 0) +
    (founderLed !== undefined ? 1 : 0) +
    (successionRisk !== undefined ? 1 : 0);
  // LEI and USt-IdNr live here rather than under "Company profile": both are
  // exact identifier lookups like euId and the register number, not
  // descriptive attributes you browse by.
  const registerCount =
    (currentEuid ? 1 : 0) +
    (currentRegisterId ? 1 : 0) +
    (currentRegisterCourt ? 1 : 0) +
    (currentRegisterType ? 1 : 0) +
    (leiCode ? 1 : 0) +
    (ustIdnr ? 1 : 0);
  return { locationCount, industryCount, sizeCount, profileCount, peopleCount, registerCount };
}

/**
 * The per-group "clear" actions. The setters are listed as dependencies only
 * because they arrive as arguments here; useState setters are stable, so each
 * callback keeps one identity for the component's lifetime, as before.
 */
export function useFilterGroupClears(values: SidebarFilterValues) {
  const {
    setSelectedCities, setSelectedBundesland, setSelectedNaceCodes, setSelectedIndustries, setTags,
    setCpvCode, setRevenueMin, setRevenueMax, setRevenueIncludeNull, setAktivaMin, setAktivaMax,
    setProfitMin, setProfitMax, setEmployeesMin, setEmployeesMax, setHasYearlyMetadata,
    setFinancialYear, setSelectedRechtsformen, setFoundedAtFrom, setFoundedAtTo,
    setSelectedMemberships, setSelectedLegalStatus, setSelectedTenderRole, setHasWebsite,
    setHasEmail, setPersonName, setSelectedAgeRanges, setFamilyManaged, setFounderLed,
    setSuccessionRisk, setCurrentEuid, setCurrentRegisterId, setCurrentRegisterCourt,
    setCurrentRegisterType, setLeiCode, setUstIdnr,
  } = values;
  const clearLocation = useCallback(() => {
    setSelectedCities([]);
    setSelectedBundesland([]);
  }, [setSelectedCities, setSelectedBundesland]);
  const clearIndustry = useCallback(() => {
    setSelectedNaceCodes([]);
    setSelectedIndustries([]);
    setTags([]);
    setCpvCode('');
  }, [setSelectedNaceCodes, setSelectedIndustries, setTags, setCpvCode]);
  const clearSize = useCallback(() => {
    setRevenueMin(undefined);
    setRevenueMax(undefined);
    setRevenueIncludeNull(false);
    setAktivaMin(undefined);
    setAktivaMax(undefined);
    setProfitMin(undefined);
    setProfitMax(undefined);
    setEmployeesMin(undefined);
    setEmployeesMax(undefined);
    setHasYearlyMetadata(false);
    setFinancialYear(undefined);
  }, [
    setRevenueMin, setRevenueMax, setRevenueIncludeNull, setAktivaMin, setAktivaMax, setProfitMin,
    setProfitMax, setEmployeesMin, setEmployeesMax, setHasYearlyMetadata, setFinancialYear,
  ]);
  const clearProfile = useCallback(() => {
    setSelectedRechtsformen([]);
    setFoundedAtFrom('');
    setFoundedAtTo('');
    setSelectedMemberships([]);
    setSelectedLegalStatus([]);
    setSelectedTenderRole([]);
    setHasWebsite(false);
    setHasEmail(false);
  }, [
    setSelectedRechtsformen, setFoundedAtFrom, setFoundedAtTo, setSelectedMemberships,
    setSelectedLegalStatus, setSelectedTenderRole, setHasWebsite, setHasEmail,
  ]);
  const clearPeople = useCallback(() => {
    setPersonName('');
    setSelectedAgeRanges([]);
    setFamilyManaged(undefined);
    setFounderLed(undefined);
    setSuccessionRisk(undefined);
  }, [setPersonName, setSelectedAgeRanges, setFamilyManaged, setFounderLed, setSuccessionRisk]);
  const clearRegister = useCallback(() => {
    setCurrentEuid('');
    setCurrentRegisterId('');
    setCurrentRegisterCourt('');
    setCurrentRegisterType('');
    setLeiCode('');
    setUstIdnr('');
  }, [setCurrentEuid, setCurrentRegisterId, setCurrentRegisterCourt, setCurrentRegisterType, setLeiCode, setUstIdnr]);
  return { clearLocation, clearIndustry, clearSize, clearProfile, clearPeople, clearRegister };
}
